import mixpanel from 'mixpanel-browser';
import { getApiUrl, getEnvironment } from 'components/utils/helper';

declare global {
    interface Window {
        mixpanel: typeof mixpanel;
    }
}

/**
 * Minimal shape of the MoConfiguration instance we depend on.
 * (MoConfiguration is plain JS; we only need getValue here.)
 */
interface MoConfigLike {
  getValue: (key: string, defaultValue?: unknown) => any;
}

/**
 * Payload produced by tracking.ts (formDataRequest).
 * Mixpanel receives `properties` as the event properties.
 */
export interface TrackingPayload {
  event: string;
  properties: Record<string, any>;
}

/**
 * Resolution state machine:
 *   - 'pending'  : remote config not loaded yet -> buffer events
 *   - 'enabled'  : Mixpanel SDK initialised      -> send via mixpanel.track()
 *   - 'disabled' : config loaded but no token    -> Mixpanel unavailable, events dropped
 */
type MixpanelState = 'pending' | 'enabled' | 'disabled';

let state: MixpanelState = 'pending';
let eventQueue: { name: string; payload: TrackingPayload }[] = [];
let pendingSuperProperties: Record<string, any> = {};

const isTruthy = (value: unknown): boolean =>
  value === true || value === 1 || value === '1' || value === 'true';

const getDeviceType = (): 'Mobile' | 'Tablet' | 'Desktop' => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'Mobile';
  if (/tablet/i.test(ua)) return 'Tablet';
  return 'Desktop';
};

/** True once mixpanel.init() has run. */
export const isMixpanelReady = (): boolean => state === 'enabled';

const dispatch = (name: string, payload: TrackingPayload): void => {
  switch (state) {
    case 'enabled':
      mixpanel.track(name, payload?.properties);
      break;
    case 'disabled':
      // Mixpanel is the only transport and no token is configured -> drop.
      break;
    default:
      // Config hasn't resolved yet. Buffer the event so nothing is lost and so
      // that early page-view events are still delivered through the SDK once
      // it is initialised (init must happen before page views — see MoRouter).
      eventQueue.push({ name, payload });
  }
};

const flushQueue = (): void => {
  const queued = eventQueue;
  eventQueue = [];
  queued.forEach(({ name, payload }) => dispatch(name, payload));
};

/**
 * Track an event through Mixpanel. Safe to call before initialisation: events
 * are buffered while the remote config is still loading and replayed on init.
 */
export const trackMixpanelEvent = (name: string, payload: TrackingPayload): void => {
  dispatch(name, payload);
};

/**
 * Initialise the Mixpanel browser SDK from app config.
 *
 * Designed to be called exactly once, as soon as the remote config has loaded
 * (see MoRouter.fetchToConfigJson), so the SDK is ready before any
 * usePageViewTracking() page-view events are sent.
 *
 * Gating: initialises only when `app.tracking.mixpanel.token` is present.
 * Without a token, Mixpanel stays uninitialised and events are dropped
 * (Mixpanel is the only transport).
 */
export const initMixpanel = (config: MoConfigLike): void => {
  // Resolve exactly once; later config changes are ignored.
  if (state !== 'pending') return;

  // Decision 10: localhost must not send into the UAT Mixpanel project.
  if (getEnvironment() === 'local') {
    state = 'disabled';
    eventQueue = [];
    pendingSuperProperties = {};
    return;
  }

  const token = config?.getValue?.('app.tracking.mixpanel.token');

  // No token configured for this locale -> Mixpanel is unavailable. Drop any
  // buffered events and stop accepting new ones (no fallback transport).
  if (!token) {
    state = 'disabled';
    eventQueue = [];
    return;
  }

  const isMixpanelDebugMode = isTruthy(config.getValue('app.tracking.mixpanel.debug'));
  const requestUrl = getApiUrl(null, null, 'tracking');

  mixpanel.init(token, {
    track_pageview: false,        // Page views are dispatched via dataLayer interception
    cross_subdomain_cookie: true, // Required for identity continuity across subdomains
    debug: isMixpanelDebugMode,   // Enable Mixpanel debug mode
    api_host: requestUrl,
    // ip is not set — SDK default sends the client IP for Mixpanel geolocation, then discards it
  });

  // Register super properties common to all page events.
  // NOTE: properties automatically captured by the SDK are intentionally omitted.
  mixpanel.register({
    device_type: getDeviceType(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    environment: getEnvironment(),
  });

  if (Object.keys(pendingSuperProperties).length > 0) {
    mixpanel.register(pendingSuperProperties);
    pendingSuperProperties = {};
  }

  // Optimizely Web writes the experiment variant via window.mixpanel. Keep after init().
  if (typeof window !== 'undefined') {
    window.mixpanel = mixpanel;
  }

  state = 'enabled';
  flushQueue();
};

/**
 * Attach super properties to every later Mixpanel event via the existing SDK
 * instance. Not identify() — does not change distinct_id.
 * Safe to call before init: properties are held and registered on init.
 */
export const registerMixpanelSuperProperties = (properties: Record<string, any>): void => {
  if (state === 'enabled') {
    mixpanel.register(properties);
    return;
  }
  if (state === 'pending') {
    pendingSuperProperties = { ...pendingSuperProperties, ...properties };
  }
};
